import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { RefObject } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Rows,
  Rows3,
  Columns3,
  Square as SquareIcon,
} from 'lucide-react';
import { getImage, setFavorite, setRating } from '../api/images';
import { imageUrl } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useKeyboard } from '../hooks/useKeyboard';
import type { ImageDetail, UserName } from '../api/types';

// ─── Types ─────────────────────────────────────────────────────────

type CompareMode = 'sxs' | 'carousel';
type RowCount = 1 | 2;

// Litet skydd: vi vill inte gå med för många bilder. Spec:en säger 2-N
// (typiskt 2-10) men en hård övre gräns gör att vi inte hänger DOM:en om
// någon klistrar in 200 IDs i URL:en.
const MAX_COMPARE = 30;

// ─── Page ──────────────────────────────────────────────────────────

export default function Compare() {
  const { shootId } = useParams<{ shootId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Parsea ids från ?ids=ulid1,ulid2,... (komma-separerat).
  const ids = useMemo(() => {
    const raw = searchParams.get('ids') ?? '';
    const list = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    // Dedup utan att tappa ordning.
    const seen = new Set<string>();
    const out: string[] = [];
    for (const id of list) {
      if (!seen.has(id)) {
        seen.add(id);
        out.push(id);
      }
    }
    return out.slice(0, MAX_COMPARE);
  }, [searchParams]);

  // ── Lokal UI-state ──
  const [mode, setMode] = useState<CompareMode>('sxs');
  const [rows, setRows] = useState<RowCount>(1);
  const [carouselIdx, setCarouselIdx] = useState(0);
  // Fokuserad bild i sxs (drivs av "närmaste centrum"). I carousel = aktuell bild.
  const [focusedId, setFocusedId] = useState<string | null>(null);

  // ── Data ──
  // Vi kan inte använda en enskild useQuery eftersom antalet bilder är dynamiskt.
  // useQueries ger oss en array av results parallellt och respekterar query-cachen
  // (om bilden redan finns från Detail/Gallery slipper vi extra fetch).
  const imageQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['image', id] as const,
      queryFn: () => getImage(id),
      enabled: !!id,
    })),
  });

  // Plocka ut färdiga bilder (i samma ordning som ids). Bilder som ännu laddar
  // representeras som null så vi kan visa skeleton för just dem.
  const images: (ImageDetail | null)[] = imageQueries.map((q) => q.data ?? null);
  const hasAnyError = imageQueries.some((q) => q.isError);
  const allLoaded = imageQueries.every((q) => !q.isLoading);

  // Säkerställ att carouselIdx aldrig är out-of-range när IDs ändras.
  useEffect(() => {
    setCarouselIdx((prev) => Math.min(prev, Math.max(0, ids.length - 1)));
  }, [ids.length]);

  // Default fokus = första bilden så 1-5 / F funkar omedelbart.
  useEffect(() => {
    if (!focusedId && ids.length > 0) {
      setFocusedId(ids[0]);
    }
  }, [ids, focusedId]);

  // ── Mutations (optimistic, samma pattern som Detail.tsx) ──
  const queryClient = useQueryClient();

  const ratingMutation = useMutation({
    mutationFn: ({ imageId, stars }: { imageId: string; stars: number }) =>
      setRating(imageId, stars),
    onMutate: async ({ imageId, stars }) => {
      if (!user) return;
      const userName = user.name as UserName;
      await queryClient.cancelQueries({ queryKey: ['image', imageId] });
      const prev = queryClient.getQueryData<ImageDetail>(['image', imageId]);
      if (prev) {
        queryClient.setQueryData<ImageDetail>(['image', imageId], {
          ...prev,
          ratings: { ...prev.ratings, [userName]: stars },
        });
      }
      // Patcha även eventuella shoot-listor (så Gallery uppdateras vid retur).
      const listQueries = queryClient.getQueriesData({
        queryKey: ['shoot', shootId, 'images'],
      });
      const listPrev: { key: readonly unknown[]; data: unknown }[] = [];
      for (const [key, data] of listQueries) {
        listPrev.push({ key, data });
        const list = data as ImageDetail[] | undefined;
        if (!list) continue;
        queryClient.setQueryData(
          key,
          list.map((it) =>
            it.id === imageId
              ? { ...it, ratings: { ...(it.ratings ?? {}), [userName]: stars } }
              : it,
          ),
        );
      }
      return { prev, listPrev };
    },
    onError: (_e, vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['image', vars.imageId], ctx.prev);
      ctx?.listPrev.forEach(({ key, data }) => queryClient.setQueryData(key, data));
    },
    onSettled: (_d, _e, vars) => {
      queryClient.invalidateQueries({ queryKey: ['image', vars.imageId] });
      queryClient.invalidateQueries({ queryKey: ['shoot', shootId, 'images'] });
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: ({ imageId, favorite }: { imageId: string; favorite: boolean }) =>
      setFavorite(imageId, favorite),
    onMutate: async ({ imageId, favorite }) => {
      if (!user) return;
      const userName = user.name as UserName;
      await queryClient.cancelQueries({ queryKey: ['image', imageId] });
      const prev = queryClient.getQueryData<ImageDetail>(['image', imageId]);
      if (prev) {
        queryClient.setQueryData<ImageDetail>(['image', imageId], {
          ...prev,
          favorites: { ...prev.favorites, [userName]: favorite },
        });
      }
      const listQueries = queryClient.getQueriesData({
        queryKey: ['shoot', shootId, 'images'],
      });
      const listPrev: { key: readonly unknown[]; data: unknown }[] = [];
      for (const [key, data] of listQueries) {
        listPrev.push({ key, data });
        const list = data as ImageDetail[] | undefined;
        if (!list) continue;
        queryClient.setQueryData(
          key,
          list.map((it) =>
            it.id === imageId
              ? { ...it, favorites: { ...(it.favorites ?? {}), [userName]: favorite } }
              : it,
          ),
        );
      }
      return { prev, listPrev };
    },
    onError: (_e, vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['image', vars.imageId], ctx.prev);
      ctx?.listPrev.forEach(({ key, data }) => queryClient.setQueryData(key, data));
    },
    onSettled: (_d, _e, vars) => {
      queryClient.invalidateQueries({ queryKey: ['image', vars.imageId] });
      queryClient.invalidateQueries({ queryKey: ['shoot', shootId, 'images'] });
    },
  });

  const rate = useCallback(
    (imageId: string, stars: number) => {
      if (!user) return;
      const idx = ids.indexOf(imageId);
      const img = idx >= 0 ? images[idx] : null;
      const cur = img?.ratings?.[user.name as UserName] ?? 0;
      ratingMutation.mutate({ imageId, stars: cur === stars ? 0 : stars });
    },
    [ids, images, user, ratingMutation],
  );

  const toggleFav = useCallback(
    (imageId: string) => {
      if (!user) return;
      const idx = ids.indexOf(imageId);
      const img = idx >= 0 ? images[idx] : null;
      const cur = img?.favorites?.[user.name as UserName] ?? false;
      favoriteMutation.mutate({ imageId, favorite: !cur });
    },
    [ids, images, user, favoriteMutation],
  );

  // ── Navigation ──
  const exit = useCallback(() => {
    navigate(`/shoots/${shootId}`);
  }, [navigate, shootId]);

  // ── Scroller-ref för side-by-side ──
  const scrollerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Scrolla sxs-listan med ungefär en "viewport-tredjedel".
  const scrollSxs = useCallback((dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.max(320, Math.round(el.clientWidth * 0.4));
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  }, []);

  // ── Keyboard shortcuts ──
  useKeyboard((e) => {
    if (ids.length === 0) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      exit();
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      setMode((m) => (m === 'sxs' ? 'carousel' : 'sxs'));
      return;
    }

    if (e.key === 'r' || e.key === 'R') {
      if (mode === 'sxs') {
        e.preventDefault();
        setRows((r) => (r === 1 ? 2 : 1));
      }
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (mode === 'carousel') {
        setCarouselIdx((i) => Math.max(0, i - 1));
      } else {
        scrollSxs(-1);
      }
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (mode === 'carousel') {
        setCarouselIdx((i) => Math.min(ids.length - 1, i + 1));
      } else {
        scrollSxs(1);
      }
      return;
    }

    // Rating + favorit på fokuserad bild.
    const targetId = mode === 'carousel' ? ids[carouselIdx] : focusedId;
    if (!targetId) return;
    const targetImage = images[ids.indexOf(targetId)];
    if (!targetImage || !user) return;

    if (/^[1-5]$/.test(e.key)) {
      e.preventDefault();
      const stars = parseInt(e.key, 10);
      rate(targetId, stars);
    } else if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      toggleFav(targetId);
    }
  });

  // ── Visible-position indicator + center-frame focus (sxs) ──
  const [visiblePos, setVisiblePos] = useState<{ first: number; last: number } | null>(
    null,
  );
  const [scrollFits, setScrollFits] = useState(true);
  const [hintDismissed, setHintDismissed] = useState(false);

  useEffect(() => {
    if (mode !== 'sxs') return;
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    if (!scroller || !track) return;

    function update() {
      if (!scroller || !track) return;
      const frames = Array.from(track.querySelectorAll<HTMLElement>('[data-frame]'));
      if (frames.length === 0) return;
      const sLeft = scroller.scrollLeft;
      const sRight = sLeft + scroller.clientWidth;
      const center = sLeft + scroller.clientWidth / 2;
      const visibleIdx: number[] = [];
      let bestId: string | null = null;
      let bestDist = Infinity;

      frames.forEach((f, i) => {
        const left = f.offsetLeft;
        const right = left + f.offsetWidth;
        // Räkna som synlig om mer än ~40px av bilden ligger i viewporten.
        if (right > sLeft + 40 && left < sRight - 40) visibleIdx.push(i);
        // Hitta bilden vars centrum är närmast scrollerns centrum.
        const fc = left + f.offsetWidth / 2;
        const dist = Math.abs(fc - center);
        if (dist < bestDist) {
          bestDist = dist;
          bestId = f.dataset.imageId ?? null;
        }
      });
      if (visibleIdx.length > 0) {
        setVisiblePos({
          first: visibleIdx[0] + 1,
          last: visibleIdx[visibleIdx.length - 1] + 1,
        });
      }
      setScrollFits(scroller.scrollWidth <= scroller.clientWidth + 4);
      if (bestId) setFocusedId(bestId);
    }

    update();
    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // Kör även en gång efter att bilder laddats (kan ändra layout).
    const timer = window.setTimeout(update, 200);

    return () => {
      scroller.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.clearTimeout(timer);
    };
  }, [mode, rows, ids.length, allLoaded]);

  // Dismissa scroll-hint vid första scroll.
  useEffect(() => {
    if (mode !== 'sxs') return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const handler = () => setHintDismissed(true);
    scroller.addEventListener('scroll', handler, { once: true, passive: true });
    return () => scroller.removeEventListener('scroll', handler);
  }, [mode]);

  // I carousel-läget = focusedId följer carouselIdx.
  useEffect(() => {
    if (mode === 'carousel' && ids[carouselIdx]) {
      setFocusedId(ids[carouselIdx]);
    }
  }, [mode, carouselIdx, ids]);

  // ── Edge cases ──
  if (ids.length < 2) {
    return <NotEnoughImages shootId={shootId!} count={ids.length} />;
  }

  // ── Render ──
  return (
    <div
      className="h-screen h-[100dvh] overflow-hidden text-loupe-text"
      style={{
        background: 'radial-gradient(ellipse at center, #0f0f0f 0%, #050505 100%)',
      }}
    >
      <TopBar
        shootId={shootId!}
        mode={mode}
        onModeChange={setMode}
        count={ids.length}
        onExit={exit}
      />

      {/* Mobile banner */}
      <div className="block sm:hidden fixed top-[110px] left-0 right-0 z-[95] px-5 py-3.5 text-center text-[12px] leading-snug bg-henrik/[0.10] border-b border-henrik/30 text-henrik">
        Compare mode is best on a{' '}
        <strong className="text-loupe-text font-semibold">larger screen</strong>. You can
        still use it here, but it is designed for desktop.
      </div>

      {hasAnyError ? (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-loupe-text-2 text-sm">
          Could not load one or more images. Try refreshing the page.
        </div>
      ) : null}

      {mode === 'sxs' ? (
        <SideBySide
          ids={ids}
          images={images}
          rows={rows}
          onRowsChange={setRows}
          focusedId={focusedId}
          visiblePos={visiblePos}
          scrollFits={scrollFits}
          hintDismissed={hintDismissed}
          scrollerRef={scrollerRef}
          trackRef={trackRef}
          onRate={rate}
          onToggleFav={toggleFav}
          viewer={user?.name as UserName | undefined}
        />
      ) : (
        <Carousel
          ids={ids}
          images={images}
          index={carouselIdx}
          onIndexChange={setCarouselIdx}
          onRate={rate}
          onToggleFav={toggleFav}
          viewer={user?.name as UserName | undefined}
        />
      )}

      <KeyboardHints mode={mode} />
    </div>
  );
}

// ─── TopBar ────────────────────────────────────────────────────────

interface TopBarProps {
  shootId: string;
  mode: CompareMode;
  onModeChange: (m: CompareMode) => void;
  count: number;
  onExit: () => void;
}

function TopBar({ shootId, mode, onModeChange, count, onExit }: TopBarProps) {
  return (
    <nav className="fixed top-0 left-0 right-0 z-[100] flex flex-wrap sm:flex-nowrap items-center justify-between px-3 sm:px-6 py-2 sm:py-0 sm:h-[60px] gap-2 bg-loupe-bg-deep/85 backdrop-blur-2xl border-b border-loupe-border">
      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
        <Link
          to={`/shoots/${shootId}`}
          className="text-loupe-text-2 hover:text-loupe-text uppercase text-[11px] sm:text-xs tracking-[0.1em] inline-flex items-center gap-1.5 transition-colors py-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Gallery
        </Link>
        <div className="hidden sm:block">
          <div className="text-[10px] tracking-[0.25em] uppercase text-loupe-text-3 font-semibold leading-tight">
            Compare Mode
          </div>
          <div className="font-display text-[15px] font-semibold tracking-tight leading-snug">
            {count} candidates
          </div>
        </div>
      </div>

      {/* Mode switcher */}
      <div className="flex gap-1 p-1 bg-loupe-bg-2 border border-loupe-border rounded-full">
        <button
          onClick={() => onModeChange('sxs')}
          className={[
            'flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-[11px] sm:text-xs font-medium tracking-wide transition-colors',
            mode === 'sxs'
              ? 'bg-white text-black'
              : 'text-loupe-text-2 hover:text-loupe-text',
          ].join(' ')}
        >
          <Columns3 className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Side-by-side</span>
          <span className="sm:hidden">Side</span>
        </button>
        <button
          onClick={() => onModeChange('carousel')}
          className={[
            'flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-[11px] sm:text-xs font-medium tracking-wide transition-colors',
            mode === 'carousel'
              ? 'bg-white text-black'
              : 'text-loupe-text-2 hover:text-loupe-text',
          ].join(' ')}
        >
          <SquareIcon className="w-3.5 h-3.5" />
          Carousel
        </button>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        <div className="px-3 py-1.5 bg-loupe-bg-2 border border-loupe-border rounded-full text-[10px] sm:text-[11px] uppercase tracking-[0.1em] text-loupe-text-2 whitespace-nowrap">
          <strong className="text-gold font-bold">{count}</strong> selected
        </div>
        <button
          onClick={onExit}
          className="bg-transparent border border-loupe-border-2 text-loupe-text px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-[11px] uppercase tracking-[0.1em] font-medium hover:bg-loupe-bg-2 transition-colors"
          title="Exit Compare (Esc)"
        >
          Exit
        </button>
      </div>
    </nav>
  );
}

// ─── Side-by-side ──────────────────────────────────────────────────

interface SxsProps {
  ids: string[];
  images: (ImageDetail | null)[];
  rows: RowCount;
  onRowsChange: (r: RowCount) => void;
  focusedId: string | null;
  visiblePos: { first: number; last: number } | null;
  scrollFits: boolean;
  hintDismissed: boolean;
  scrollerRef: RefObject<HTMLDivElement>;
  trackRef: RefObject<HTMLDivElement>;
  onRate: (id: string, stars: number) => void;
  onToggleFav: (id: string) => void;
  viewer: UserName | undefined;
}

function SideBySide({
  ids,
  images,
  rows,
  onRowsChange,
  focusedId,
  visiblePos,
  scrollFits,
  hintDismissed,
  scrollerRef,
  trackRef,
  onRate,
  onToggleFav,
  viewer,
}: SxsProps) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Scroll hint */}
      {!scrollFits && !hintDismissed ? (
        <div className="hidden sm:flex fixed top-[76px] right-6 z-[85] items-center gap-2 px-3.5 py-2 bg-loupe-bg-2/85 backdrop-blur-xl border border-loupe-border-2 rounded-full text-[10px] uppercase tracking-[0.15em] text-loupe-text-2 pointer-events-none animate-pulse">
          ← Scroll to see all →
        </div>
      ) : null}

      <div
        ref={scrollerRef}
        className="h-full overflow-x-auto overflow-y-hidden p-4 pb-24 scrollbar-thin"
        style={{
          scrollSnapType: 'x proximity',
          WebkitOverflowScrolling: 'touch',
          paddingTop: '76px',
        }}
      >
        <div
          ref={trackRef}
          className="h-full gap-3"
          style={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: 'max-content',
            gridTemplateRows: rows === 2 ? '1fr 1fr' : '1fr',
          }}
        >
          {ids.map((id, idx) => {
            const img = images[idx];
            return (
              <SxsFrame
                key={id}
                id={id}
                image={img}
                focused={focusedId === id}
                onRate={(s) => onRate(id, s)}
                onToggleFav={() => onToggleFav(id)}
                viewer={viewer}
              />
            );
          })}
        </div>
      </div>

      {/* Floating bottom controls: rows toggle + position indicator */}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[90] flex items-center gap-3 px-3.5 py-2 bg-loupe-bg-2/95 backdrop-blur-2xl border border-loupe-border-2 rounded-full shadow-[0_12px_40px_-8px_rgba(0,0,0,0.7)]">
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-loupe-text-3">
          Rows
        </span>
        <div className="flex gap-0.5 p-0.5 bg-loupe-bg-3 border border-loupe-border-2 rounded-full">
          <button
            onClick={() => onRowsChange(1)}
            className={[
              'flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] tracking-wide transition-colors',
              rows === 1
                ? 'bg-white text-black'
                : 'text-loupe-text-2 hover:text-loupe-text',
            ].join(' ')}
            title="One row"
          >
            <Rows className="w-3 h-3" />1
          </button>
          <button
            onClick={() => onRowsChange(2)}
            className={[
              'flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] tracking-wide transition-colors',
              rows === 2
                ? 'bg-white text-black'
                : 'text-loupe-text-2 hover:text-loupe-text',
            ].join(' ')}
            title="Two rows"
          >
            <Rows3 className="w-3 h-3" />2
          </button>
        </div>
        <div className="w-px h-4 bg-loupe-border-2" />
        <div className="font-mono text-[10px] text-loupe-text-3 tabular-nums tracking-wide">
          <strong className="text-loupe-text font-semibold">
            {visiblePos
              ? visiblePos.first === visiblePos.last
                ? `${visiblePos.first}`
                : `${visiblePos.first}–${visiblePos.last}`
              : '—'}
          </strong>{' '}
          of {ids.length}
        </div>
      </div>
    </div>
  );
}

// ─── Sxs frame (en bild) ───────────────────────────────────────────

interface SxsFrameProps {
  id: string;
  image: ImageDetail | null;
  focused: boolean;
  onRate: (stars: number) => void;
  onToggleFav: () => void;
  viewer: UserName | undefined;
}

function SxsFrame({ id, image, focused, onRate, onToggleFav, viewer }: SxsFrameProps) {
  // Aspect ratio krävs för att grid-tracken ska kunna räkna ut rätt bredd
  // innan bilden laddat (annars kollapsar containern till 0 px).
  const aspect = image?.width && image?.height ? image.width / image.height : 0.66;

  return (
    <div
      data-frame
      data-image-id={id}
      className={[
        'relative h-full bg-loupe-bg-2 rounded-lg overflow-hidden transition-shadow duration-200',
        'flex items-center justify-center',
        focused
          ? 'ring-2 ring-white/50 shadow-[0_0_0_2px_rgba(255,255,255,0.4),0_12px_40px_-10px_rgba(0,0,0,0.7)]'
          : 'shadow-[0_12px_40px_-10px_rgba(0,0,0,0.7)]',
      ].join(' ')}
      style={{
        aspectRatio: aspect,
        scrollSnapAlign: 'center',
      }}
    >
      {image ? (
        <img
          src={imageUrl(image.id, 'preview')}
          alt={image.filename}
          className="h-full w-auto block object-contain"
          loading="eager"
          decoding="async"
        />
      ) : (
        <div className="h-full w-full bg-loupe-bg-3 animate-pulse" />
      )}

      {/* Filename pill (top-left) */}
      {image ? (
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/65 backdrop-blur-xl border border-white/10 rounded-full font-mono text-[11px] text-loupe-text z-10">
          {image.filename}
        </div>
      ) : null}

      {/* Rate overlay (gradient + per-user rows) */}
      <div
        className="absolute bottom-0 left-0 right-0 p-3 pt-5 flex flex-col gap-2 z-10 pointer-events-none"
        style={{
          background:
            'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 50%, transparent 100%)',
        }}
      >
        {(['henrik', 'michelle'] as UserName[]).map((u) => {
          const stars = image?.ratings?.[u] ?? 0;
          const fav = image?.favorites?.[u] ?? false;
          const isViewer = viewer === u;
          return (
            <RateRow
              key={u}
              user={u}
              stars={stars}
              favorite={fav}
              isViewer={isViewer}
              onRate={onRate}
              onToggleFav={onToggleFav}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── RateRow ───────────────────────────────────────────────────────

interface RateRowProps {
  user: UserName;
  stars: number;
  favorite: boolean;
  isViewer: boolean;
  onRate: (stars: number) => void;
  onToggleFav: () => void;
}

function RateRow({
  user,
  stars,
  favorite,
  isViewer,
  onRate,
  onToggleFav,
}: RateRowProps) {
  const isHenrik = user === 'henrik';
  return (
    <div className="flex items-center gap-2 bg-black/55 backdrop-blur-xl border border-white/10 px-2.5 py-1.5 rounded-full text-xs w-fit pointer-events-auto">
      <div className="flex items-center gap-1.5 font-medium text-[11px]">
        <span
          className={`w-1.5 h-1.5 rounded-full ${isHenrik ? 'bg-henrik' : 'bg-michelle'}`}
        />
        <span className={isViewer ? 'text-loupe-text' : 'text-loupe-text-2'}>
          {isHenrik ? 'H' : 'M'}
        </span>
      </div>
      <div className="flex items-center gap-px">
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= stars;
          return (
            <button
              key={n}
              type="button"
              disabled={!isViewer}
              onClick={(e) => {
                e.stopPropagation();
                if (isViewer) onRate(n);
              }}
              aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
              className={[
                'text-[14px] leading-none px-px transition-transform',
                filled ? 'text-gold' : 'text-loupe-text-3',
                isViewer
                  ? 'cursor-pointer hover:scale-125'
                  : 'cursor-default opacity-90',
              ].join(' ')}
            >
              ★
            </button>
          );
        })}
      </div>
      <button
        type="button"
        disabled={!isViewer}
        onClick={(e) => {
          e.stopPropagation();
          if (isViewer) onToggleFav();
        }}
        aria-label="Favorite"
        className={[
          'text-[15px] leading-none ml-1 transition-transform',
          favorite ? 'text-heart' : 'text-loupe-text-3',
          isViewer ? 'cursor-pointer hover:scale-125' : 'cursor-default opacity-90',
        ].join(' ')}
      >
        ♥
      </button>
    </div>
  );
}

// ─── Carousel ──────────────────────────────────────────────────────

interface CarouselProps {
  ids: string[];
  images: (ImageDetail | null)[];
  index: number;
  onIndexChange: (i: number) => void;
  onRate: (id: string, stars: number) => void;
  onToggleFav: (id: string) => void;
  viewer: UserName | undefined;
}

function Carousel({
  ids,
  images,
  index,
  onIndexChange,
  onRate,
  onToggleFav,
  viewer,
}: CarouselProps) {
  const current = images[index];
  const currentId = ids[index];
  const thumbStripRef = useRef<HTMLDivElement>(null);

  // Scrolla aktiv tumnagel till mitten av strippen.
  useEffect(() => {
    const strip = thumbStripRef.current;
    if (!strip) return;
    const active = strip.querySelector<HTMLElement>('[data-active="true"]');
    if (active) {
      active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [index]);

  const goPrev = () => onIndexChange(Math.max(0, index - 1));
  const goNext = () => onIndexChange(Math.min(ids.length - 1, index + 1));

  return (
    <div
      className="absolute inset-0 flex flex-col"
      style={{ paddingTop: '60px' }}
    >
      {/* Stage */}
      <div className="flex-1 relative flex items-center justify-center px-12 sm:px-20 py-6 min-h-0">
        {/* Filename + position pill */}
        {current ? (
          <div className="absolute top-4 sm:top-6 left-1/2 -translate-x-1/2 px-3.5 py-1.5 bg-loupe-bg-2/85 backdrop-blur-xl border border-loupe-border-2 rounded-full font-mono text-[11px] text-loupe-text-2 flex items-center gap-2.5 z-10">
            <span>{current.filename}</span>
            <span className="text-loupe-text-3">·</span>
            <span className="text-gold font-semibold tabular-nums">
              {index + 1} of {ids.length}
            </span>
          </div>
        ) : null}

        {/* Prev/Next arrows */}
        {index > 0 ? (
          <button
            onClick={goPrev}
            aria-label="Previous image"
            className="absolute top-1/2 -translate-y-1/2 left-3 sm:left-6 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-loupe-bg-2/85 backdrop-blur-xl border border-loupe-border-2 text-loupe-text flex items-center justify-center hover:scale-105 hover:bg-loupe-bg-3/95 transition-all z-20"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : null}
        {index < ids.length - 1 ? (
          <button
            onClick={goNext}
            aria-label="Next image"
            className="absolute top-1/2 -translate-y-1/2 right-3 sm:right-6 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-loupe-bg-2/85 backdrop-blur-xl border border-loupe-border-2 text-loupe-text flex items-center justify-center hover:scale-105 hover:bg-loupe-bg-3/95 transition-all z-20"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        ) : null}

        {/* Image */}
        {current ? (
          <img
            key={current.id}
            src={imageUrl(current.id, 'preview')}
            alt={current.filename}
            className="max-w-full max-h-full object-contain rounded shadow-[0_20px_60px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.05)]"
            style={{
              aspectRatio:
                current.width && current.height
                  ? `${current.width} / ${current.height}`
                  : undefined,
            }}
            loading="eager"
            decoding="async"
          />
        ) : (
          <div className="text-loupe-text-3 text-sm">Loading…</div>
        )}

        {/* Rating stack */}
        {current && currentId ? (
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-wrap justify-center gap-2 z-10">
            {(['henrik', 'michelle'] as UserName[]).map((u) => (
              <CarouselRateRow
                key={u}
                user={u}
                stars={current.ratings?.[u] ?? 0}
                favorite={current.favorites?.[u] ?? false}
                isViewer={viewer === u}
                onRate={(s) => onRate(currentId, s)}
                onToggleFav={() => onToggleFav(currentId)}
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* Thumbstrip */}
      <div
        ref={thumbStripRef}
        className="h-[120px] sm:h-[130px] flex-shrink-0 bg-loupe-bg-deep/95 border-t border-loupe-border px-4 sm:px-6 py-3 flex items-center gap-2 overflow-x-auto overflow-y-hidden scrollbar-thin"
      >
        {ids.map((id, i) => {
          const img = images[i];
          const active = i === index;
          const maxStars = Math.max(
            img?.ratings?.henrik ?? 0,
            img?.ratings?.michelle ?? 0,
          );
          const anyFav = !!(img?.favorites?.henrik || img?.favorites?.michelle);
          return (
            <button
              key={id}
              type="button"
              data-active={active}
              onClick={() => onIndexChange(i)}
              className={[
                'relative w-[80px] sm:w-[90px] h-[100px] flex-shrink-0 rounded-md overflow-hidden bg-loupe-bg-2 cursor-pointer transition-all duration-200',
                'border-2',
                active
                  ? 'border-white opacity-100 scale-105'
                  : 'border-transparent opacity-55 hover:opacity-90',
              ].join(' ')}
            >
              {img ? (
                <img
                  src={imageUrl(img.id, 'thumb')}
                  alt={img.filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-loupe-bg-3 animate-pulse" />
              )}
              <span className="absolute top-1 right-1.5 font-mono text-[9px] text-white/85 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
                {String(i + 1).padStart(2, '0')}
              </span>
              {anyFav ? (
                <span className="absolute top-1 left-1.5 text-[11px] text-heart drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
                  ♥
                </span>
              ) : null}
              {maxStars > 0 ? (
                <span className="absolute bottom-1 left-1.5 text-[10px] text-gold tracking-tighter drop-shadow-[0_1px_3px_rgba(0,0,0,0.95)]">
                  {'★'.repeat(maxStars)}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── CarouselRateRow ───────────────────────────────────────────────

function CarouselRateRow({
  user,
  stars,
  favorite,
  isViewer,
  onRate,
  onToggleFav,
}: {
  user: UserName;
  stars: number;
  favorite: boolean;
  isViewer: boolean;
  onRate: (stars: number) => void;
  onToggleFav: () => void;
}) {
  const isHenrik = user === 'henrik';
  return (
    <div className="flex items-center gap-2 bg-loupe-bg-2/95 backdrop-blur-xl border border-loupe-border-2 px-3 py-1.5 rounded-full text-xs">
      <div className="flex items-center gap-1.5 font-medium text-[11px]">
        <span
          className={`w-1.5 h-1.5 rounded-full ${isHenrik ? 'bg-henrik' : 'bg-michelle'}`}
        />
        {isHenrik ? 'Henrik' : 'Michelle'}
      </div>
      <div className="flex items-center gap-px">
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= stars;
          return (
            <button
              key={n}
              type="button"
              disabled={!isViewer}
              onClick={() => isViewer && onRate(n)}
              aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
              className={[
                'text-[15px] leading-none px-px transition-transform',
                filled ? 'text-gold' : 'text-loupe-text-3',
                isViewer
                  ? 'cursor-pointer hover:scale-125'
                  : 'cursor-default opacity-90',
              ].join(' ')}
            >
              ★
            </button>
          );
        })}
      </div>
      <button
        type="button"
        disabled={!isViewer}
        onClick={() => isViewer && onToggleFav()}
        aria-label="Favorite"
        className={[
          'text-[16px] leading-none ml-1 transition-transform',
          favorite ? 'text-heart' : 'text-loupe-text-3',
          isViewer ? 'cursor-pointer hover:scale-125' : 'cursor-default opacity-90',
        ].join(' ')}
      >
        ♥
      </button>
    </div>
  );
}

// ─── Keyboard hints (desktop only) ─────────────────────────────────

function KeyboardHints({ mode }: { mode: CompareMode }) {
  return (
    <div className="hidden lg:flex fixed bottom-5 left-5 z-[90] gap-3 px-3.5 py-2 bg-loupe-bg-2/90 backdrop-blur-xl border border-loupe-border-2 rounded-full text-[10px] text-loupe-text-2 pointer-events-none">
      <span>
        <span className="kbd">←→</span> {mode === 'sxs' ? 'scroll' : 'nav'}
      </span>
      <span>
        <span className="kbd">1-5</span> rate
      </span>
      <span>
        <span className="kbd">F</span> heart
      </span>
      <span>
        <span className="kbd">Tab</span> mode
      </span>
      {mode === 'sxs' ? (
        <span>
          <span className="kbd">R</span> rows
        </span>
      ) : null}
      <span>
        <span className="kbd">Esc</span> back
      </span>
    </div>
  );
}

// ─── Empty / error state ───────────────────────────────────────────

function NotEnoughImages({ shootId, count }: { shootId: string; count: number }) {
  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col items-center justify-center text-center gap-4 px-6">
      <div className="font-display text-3xl">Need at least 2 images to compare</div>
      <p className="text-loupe-text-2 text-sm max-w-md">
        {count === 0
          ? 'No images were passed to compare mode. Go back and select 2 or more frames in the gallery.'
          : 'Compare mode is meant for side-by-side review. Pick another frame to compare against.'}
      </p>
      <Link
        to={`/shoots/${shootId}`}
        className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wider hover:bg-neutral-200 transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to gallery
      </Link>
    </div>
  );
}
